import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PlusCircle, TrendingUp, Trophy, Award, FileText, ArrowRight } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { STATUS_COLORS, eur } from "@/lib/constants";
import api from "@/lib/api";

const LEVEL_STYLE = {
  base: { label: "Segnalatore", icon: Award, color: "text-slate-600 bg-slate-100" },
  attivo: { label: "Segnalatore Attivo", icon: TrendingUp, color: "text-blue-700 bg-blue-100" },
  master: { label: "Master Segnalatore", icon: Trophy, color: "text-[#8a6d0f] bg-[#D4AF37]/20" },
};

function Stat({ label, value, hint, gold }) {
  return (
    <div className={`rounded-xl border p-5 card-hover ${gold ? "border-[#D4AF37]/40 bg-[#D4AF37]/5" : "border-slate-200 bg-white"}`}>
      <p className="text-sm text-slate-500">{label}</p>
      <p className={`mt-2 font-display text-3xl font-semibold ${gold ? "text-[#8a6d0f]" : "text-slate-900"}`}>{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [refs, setRefs] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/referrals").then((r) => setRefs(r.data)).catch(() => {});
  }, []);

  const level = summary?.level?.key || "base";
  const LevelIcon = LEVEL_STYLE[level].icon;

  return (
    <DashboardLayout title="La mia dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium w-fit ${LEVEL_STYLE[level].color}`} data-testid="gamification-level">
          <LevelIcon className="h-4 w-4" /> {LEVEL_STYLE[level].label}
        </div>
        <Link to="/segnala" data-testid="dashboard-segnala-btn"
          className="btn-gold rounded-full px-6 py-3 font-semibold inline-flex items-center gap-2 w-fit">
          <PlusCircle className="h-5 w-5" /> Segnala un immobile
        </Link>
      </div>

      {summary && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Le mie segnalazioni" value={summary.total} hint={`${summary.active} attive`} />
            <Stat label="Incarichi acquisiti" value={summary.incarichi} />
            <Stat label="Affari conclusi" value={summary.deals} />
            <Stat label="Merito fidelizzazione maturato" value={eur(summary.commissions.maturato)} gold />
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <p className="text-sm text-slate-500">Potenziale <span className="text-xs text-slate-400">(non ancora maturato)</span></p>
              <p className="mt-1 font-display text-2xl font-semibold text-amber-600">{eur(summary.commissions.potenziale)}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <p className="text-sm text-slate-500">In pagamento</p>
              <p className="mt-1 font-display text-2xl font-semibold text-blue-600">{eur(summary.commissions.in_pagamento)}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <p className="text-sm text-slate-500">Pagato</p>
              <p className="mt-1 font-display text-2xl font-semibold text-[#8a6d0f]">{eur(summary.commissions.pagato)}</p>
            </div>
          </div>
        </>
      )}

      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-semibold text-slate-900">Le mie segnalazioni</h2>
          <Link to="/compensi" className="text-sm text-[#8a6d0f] font-medium inline-flex items-center gap-1">
            Merito fidelizzazione <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {refs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center">
            <FileText className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="mt-3 text-slate-500">Non hai ancora fatto segnalazioni.</p>
            <Link to="/segnala" className="btn-gold mt-4 inline-flex rounded-full px-6 py-3 font-semibold">Fai la prima segnalazione</Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {refs.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
                <Link to={`/segnalazioni/${r.id}`} data-testid={`referral-card-${r.code}`}
                  className="block rounded-xl border border-slate-200 bg-white p-5 card-hover">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-sm font-semibold text-slate-900">{r.code}</span>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-600"}`}>
                      {r.closed ? "Conclusa" : r.status_label}
                    </span>
                  </div>
                  <p className="mt-3 font-medium text-slate-800">{r.property_type} · {r.comune}</p>
                  <div className="mt-4 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-[#D4AF37]" style={{ width: `${r.closed ? 100 : (r.stage / r.stages.length) * 100}%` }} />
                  </div>
                  <p className="mt-2 text-xs text-slate-400">{r.closed ? "Percorso concluso" : `Fase ${r.stage} di ${r.stages.length}`}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
